import { useNavigate, useParams } from 'react-router';
import { useIntl } from 'react-intl';
import routes from '@commons/routes';
import { notifications } from '@mantine/notifications';
import { Container, Flex, NativeSelect, Textarea, Title } from '@mantine/core';

import { Button, TextInput } from '@mantine/core';
import { ScriptTagsWarning } from '@components/ScriptTagsWarning';
import { TemplateInfoAlert } from '@components/TemplateInfoAlert';
import { CodeEditor } from '@components/CodeEditor';
import { useForm } from '@mantine/form';
import { FormBuilder } from '@components/FormBuilder';
import { useCallback, useState } from 'react';
import { useAppDispatch } from '@store';
import { createModel } from '@store/slices/modelsSlice';

export const CreateModel = () => {
  const navigate = useNavigate();
  const { username } = useParams();
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const [templateValue, setTemplateValue] = useState<string>('');

  const form: any = useForm({
    mode: 'uncontrolled',
    initialValues: {
      name: '',
      type: 'CONTRACT',
      category: '',
      template: '',
      description: '',
      fields: [],
    },

    validate: {
      name: (value: string) => {
        if (!value) {
          return intl.formatMessage({ id: 'required' });
        }
        return null;
      },
      type: (value: string) => {
        if (!value) {
          return intl.formatMessage({ id: 'required' });
        }
        return null;
      },
      category: (value: string) => {
        if (!value) {
          return intl.formatMessage({ id: 'required' });
        }
        return null;
      },
    },
  });

  const handleSubmit = async (data: any) => {
    if (!username) {
      return;
    }

    try {
      const result = await dispatch(
        createModel({ username, modelData: data })
      ).unwrap();

      notifications.show({
        title: intl.formatMessage({ id: 'success' }),
        message: intl.formatMessage(
          { id: 'createdModel' },
          { name: result?.name }
        ),
        color: 'green',
      });

      navigate(routes.routes.models(username));
    } catch (err: any) {
      notifications.show({
        title: intl.formatMessage({ id: 'error' }),
        message: err?.message || intl.formatMessage({ id: 'error' }),
        color: 'red',
      });
    }
  };

  const handleFieldsChange = useCallback(
    (fields: any[]) => {
      form.setFieldValue('fields', fields);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // Handle template change
  const handleTemplateChange = (value: string) => {
    setTemplateValue(value);
    form.setFieldValue('template', value);
  };

  return (
    <Container my="md" size="xl">
      <Title order={4} mb="md">
        {intl.formatMessage({ id: 'addModel' })}
      </Title>

      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Flex gap="md" direction="column">
          <TextInput
            withAsterisk
            name="name"
            label={intl.formatMessage({ id: 'name' })}
            placeholder={intl.formatMessage({ id: 'name' })}
            key={form.key('name')}
            {...form.getInputProps('name')}
          />

          <Textarea
            minRows={3}
            name="description"
            label={intl.formatMessage({ id: 'description' })}
            placeholder={intl.formatMessage({ id: 'description' })}
            key={form.key('description')}
            {...form.getInputProps('description')}
          />

          <NativeSelect
            withAsterisk
            name="type"
            label={intl.formatMessage({ id: 'type' })}
            data={[
              {
                label: intl.formatMessage({ id: 'contract' }),
                value: 'CONTRACT',
              },
              { label: intl.formatMessage({ id: 'annex' }), value: 'ANNEX' },
            ]}
            {...form.getInputProps('type')}
          />

          <TextInput
            withAsterisk
            name="category"
            label={intl.formatMessage({ id: 'category' })}
            placeholder={intl.formatMessage({ id: 'category' })}
            key={form.key('category')}
            {...form.getInputProps('category')}
          />

          <FormBuilder
            fields={form.values?.fields || []}
            onFieldsChange={handleFieldsChange}
          />

          <TemplateInfoAlert fields={form.values?.fields || []} />

          <ScriptTagsWarning template={templateValue} />

          <CodeEditor
            value={templateValue}
            onChange={handleTemplateChange}
            label={intl.formatMessage({ id: 'template' })}
            placeholder={intl.formatMessage({ id: 'template' })}
          />

          <Button fullWidth type="submit" variant="primary">
            {intl.formatMessage({ id: 'create' })}
          </Button>
        </Flex>
      </form>
    </Container>
  );
};
